<script setup>
import { useHelpersStore } from "@/aygulyazilim/store/useHelpers"
import { requiredValidator } from '@core/utils/validators'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useKysTedarikciPerformansStore } from "./useKysTedarikciPerformansStore"

const router = useRouter()
const storeHelpers = useHelpersStore()
const store = useKysTedarikciPerformansStore()

const isFormValid = ref(false)
const refForm = ref()
const isEdit = ref(false)
const kayitUuid = ref(null)

const form = ref({
  tedarikci_uuid: null,
  degerlendirme_donemi: '',
  toplam_satinalma_sayisi: 0,
  uygunsuzluk_sayisi: 0,
  fiyat_ve_vade_puani: 0,
  zamaninda_teslimat_puani: 0,
  urun_kalitesi_puani: 0,
  hizmet_kalitesi_puani: 0,
  guvenilirlik_puani: 0,
  toplam_puan: 0,
  ilgili_notlar: '',
  karar: '',
})

const tedarikciListesi = computed(() => store.tedarikciListesi)
const loading = computed(() => store.loading)

// Computed for showing selected tedarikci details
const selectedTedarikci = computed(() => {
  if (!form.value.tedarikci_uuid) return null
  
  return tedarikciListesi.value.find(t => t.uuid === form.value.tedarikci_uuid)
})

// Auto calculate total points
watch([
  () => form.value.fiyat_ve_vade_puani,
  () => form.value.zamaninda_teslimat_puani,
  () => form.value.urun_kalitesi_puani,
  () => form.value.hizmet_kalitesi_puani,
  () => form.value.guvenilirlik_puani,
], () => {
  const total = Number(form.value.fiyat_ve_vade_puani) +
                Number(form.value.zamaninda_teslimat_puani) +
                Number(form.value.urun_kalitesi_puani) +
                Number(form.value.hizmet_kalitesi_puani) +
                Number(form.value.guvenilirlik_puani)
  
  form.value.toplam_puan = total
})

const kararOptions = [
  'FİRMA ONAYLI TED. LİST. KALMAYA DEVAM ETSİN',
  'FİRMAYA UYARI YAZISI YAZILSIN',
  'FİRMA ONAYLI TEDARİKÇİ LİSTESİNDEN ÇIKARILSIN',
]

onMounted(async () => {
  await store.fetchTedarikciListesi()

  const currentRoute = router.currentRoute.value
  if (currentRoute.params.uuid) {
    isEdit.value = true
    kayitUuid.value = currentRoute.params.uuid
    try {
      const detail = await store.fetchKayitDetay(kayitUuid.value)
      
      form.value = {
        tedarikci_uuid: detail.tedarikci_uuid,
        degerlendirme_donemi: detail.degerlendirme_donemi,
        toplam_satinalma_sayisi: detail.toplam_satinalma_sayisi,
        uygunsuzluk_sayisi: detail.uygunsuzluk_sayisi,
        fiyat_ve_vade_puani: detail.fiyat_ve_vade_puani,
        zamaninda_teslimat_puani: detail.zamaninda_teslimat_puani,
        urun_kalitesi_puani: detail.urun_kalitesi_puani,
        hizmet_kalitesi_puani: detail.hizmet_kalitesi_puani,
        guvenilirlik_puani: detail.guvenilirlik_puani,
        toplam_puan: detail.toplam_puan,
        ilgili_notlar: detail.ilgili_notlar,
        karar: detail.karar,
      }
    } catch (error) {
      storeHelpers.toast('error', 'Kayıt bilgileri alınamadı.')
      router.push({ name: 'kys-tedarikci-performans' })
    }
  }
})

const onSubmit = () => {
  refForm.value?.validate().then(({ valid }) => {
    if (valid) {
      store.saveKayit(form.value, kayitUuid.value)
        .then(() => {
          storeHelpers.toast('success', 'Kayıt başarıyla kaydedildi.')
          router.push({ name: 'kys-tedarikci-performans' })
        })
        .catch(err => {
          storeHelpers.toast('error', 'Kayıt işlemi başarısız oldu.')
        })
    }
  })
}
</script>

<template>
  <VCard class="pa-4">
    <VCardItem class="pb-2">
      <div class="d-flex align-center gap-4">
        <VBtn
          icon
          variant="tonal"
          color="secondary"
          size="small"
          :to="{ name: 'kys-tedarikci-performans' }"
        >
          <VIcon icon="tabler-arrow-left" />
        </VBtn>
        <VCardTitle class="text-h5 font-weight-bold">
          {{ isEdit ? 'Tedarikçi Performans Düzelt' : 'Yeni Tedarikçi Performans Değerlendirmesi' }}
        </VCardTitle>
      </div>
    </VCardItem>

    <VDivider class="my-4" />

    <VCardText>
      <VForm ref="refForm" v-model="isFormValid" @submit.prevent="onSubmit">
        <VRow class="mb-4">
          <VCol cols="12">
            <h3 class="text-h6 font-weight-bold text-primary">
              Tedarikçi ve Dönem Bilgileri
            </h3>
          </VCol>
          <VCol cols="12" md="6">
            <AppAutocomplete
              v-model="form.tedarikci_uuid"
              :items="tedarikciListesi"
              label="Tedarikçi Adı"
              item-title="tedarikci_adi"
              item-value="uuid"
              :rules="[requiredValidator]"
              clearable
            />
          </VCol>
          <VCol cols="12" md="6">
            <AppTextField
              v-model="form.degerlendirme_donemi"
              label="Değerlendirme Dönemi (Örn: 2026/1. Altı Ay)"
              :rules="[requiredValidator]"
            />
          </VCol>
        </VRow>

        <VRow v-if="selectedTedarikci" class="mb-6 bg-var-theme-background pa-2 rounded">
          <VCol cols="12" md="4">
            <strong>Adres:</strong> {{ selectedTedarikci.adres }}
          </VCol>
          <VCol cols="12" md="4">
            <strong>İletişim:</strong> {{ selectedTedarikci.tel_no }}
          </VCol>
          <VCol cols="12" md="4">
            <strong>Yetkili:</strong> {{ selectedTedarikci.yetkili_kisi }}
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <VRow class="mb-4">
          <VCol cols="12">
            <h3 class="text-h6 font-weight-bold text-primary">
              İstatistikler
            </h3>
          </VCol>
          <VCol cols="12" md="6">
            <AppTextField
              v-model.number="form.toplam_satinalma_sayisi"
              label="Dönem İçerisinde Toplam Satınalma Sayısı"
              type="number"
            />
          </VCol>
          <VCol cols="12" md="6">
            <AppTextField
              v-model.number="form.uygunsuzluk_sayisi"
              label="Dönem İçerisindeki Satınalmalarda Oluşan Uygunsuzluk Sayısı"
              type="number"
            />
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <VRow class="mb-4">
          <VCol cols="12">
            <h3 class="text-h6 font-weight-bold text-primary">
              Değerlendirme Kriterleri
            </h3>
          </VCol>

          <VCol cols="12" md="6">
            <VCard variant="outlined" class="pa-3">
              <div class="font-weight-medium mb-2">
                1. Fiyat Uygunluğu ve Ödeme Vadesi
              </div>
              <VRadioGroup v-model="form.fiyat_ve_vade_puani" inline>
                <VRadio label="İYİ (15 Puan)" :value="15" />
                <VRadio label="ORTA (10 Puan)" :value="10" />
                <VRadio label="KÖTÜ (0 Puan)" :value="0" />
              </VRadioGroup>
            </VCard>
          </VCol>

          <VCol cols="12" md="6">
            <VCard variant="outlined" class="pa-3">
              <div class="font-weight-medium mb-2">
                2. Zamanında Teslimat
              </div>
              <VRadioGroup v-model="form.zamaninda_teslimat_puani" inline>
                <VRadio label="İYİ (15 Puan)" :value="15" />
                <VRadio label="ORTA (10 Puan)" :value="10" />
                <VRadio label="KÖTÜ (0 Puan)" :value="0" />
              </VRadioGroup>
            </VCard>
          </VCol>

          <VCol cols="12" md="6">
            <VCard variant="outlined" class="pa-3">
              <div class="font-weight-medium mb-2">
                3. Ürün Kalitesi
              </div>
              <VRadioGroup v-model="form.urun_kalitesi_puani" inline>
                <VRadio label="İYİ (30 Puan)" :value="30" />
                <VRadio label="ORTA (10 Puan)" :value="10" />
                <VRadio label="KÖTÜ (0 Puan)" :value="0" />
              </VRadioGroup>
            </VCard>
          </VCol>

          <VCol cols="12" md="6">
            <VCard variant="outlined" class="pa-3">
              <div class="font-weight-medium mb-2">
                4. Satın Alma ve Sonrası Hizmet Kalitesi
              </div>
              <VRadioGroup v-model="form.hizmet_kalitesi_puani" inline>
                <VRadio label="İYİ (20 Puan)" :value="20" />
                <VRadio label="ORTA (10 Puan)" :value="10" />
                <VRadio label="KÖTÜ (0 Puan)" :value="0" />
              </VRadioGroup>
            </VCard>
          </VCol>

          <VCol cols="12" md="6">
            <VCard variant="outlined" class="pa-3">
              <div class="font-weight-medium mb-2">
                5. Firmanın Güvenilir Olması
              </div>
              <VRadioGroup v-model="form.guvenilirlik_puani" inline>
                <VRadio label="İYİ (20 Puan)" :value="20" />
                <VRadio label="ORTA (10 Puan)" :value="10" />
                <VRadio label="KÖTÜ (0 Puan)" :value="0" />
              </VRadioGroup>
            </VCard>
          </VCol>
          
          <VCol cols="12" md="6" class="d-flex align-center justify-center">
            <VCard color="primary" variant="tonal" class="pa-4 text-center w-100">
              <div class="text-h6">
                Tedarikçi Performans Puanı
              </div>
              <div class="text-h3 font-weight-bold">
                {{ form.toplam_puan }} / 100
              </div>
            </VCard>
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <VRow class="mb-6">
          <VCol cols="12">
            <AppTextarea
              v-model="form.ilgili_notlar"
              label="İlgili Notlar"
              rows="3"
            />
          </VCol>
          <VCol cols="12">
            <AppSelect
              v-model="form.karar"
              :items="kararOptions"
              label="Karar (Genel Müdür)"
              clearable
            />
          </VCol>
        </VRow>

        <div class="d-flex justify-end gap-2 mt-4">
          <VBtn color="secondary" variant="tonal" :to="{ name: 'kys-tedarikci-performans' }">
            {{ $t('Vazgeç') }}
          </VBtn>
          <VBtn color="primary" type="submit" :loading="loading" :disabled="loading">
            {{ $t('Kaydet') }}
          </VBtn>
        </div>
      </VForm>
    </VCardText>
  </VCard>
</template>
